import type {ValueOrderDocument} from "@reva/shared/value-order";

import {
    DocumentDownloadApiInterface,
    DocumentsListApiInterface,
    DocumentUploadApiInterface,
} from "../api/interface/documents";
import {ErrorI18n} from "../i18n";
import {generateDefaultQueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import {selectDocuments, selectInitialized, setDocuments, setLoading, setQueryError} from "./documentsSlice";
import {selectRevaId} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";
import {selectHasValueOrders} from "./valueOrdersSlice";

/**
 * Async thunk to load Value-Order-API documents.
 */
export const loadDocumentsAsync = (): RevaThunkAction<Promise<void>> => async (dispatch, getState, extraArgument) => {
    const {serviceApi} = extraArgument;

    const state = getState();

    const revaId = selectRevaId(state);
    const hasValueOrders = selectHasValueOrders(state);
    const credentials = selectCredentials(state);

    if (!revaId || !hasValueOrders) {
        dispatch(
            setQueryError({
                error: "documents.error.noValidValuation",
                errorArgs: {},
                errorHasContactLink: false,
            }),
        );
        return;
    }

    dispatch(setLoading());

    try {
        const documents = await new DocumentsListApiInterface(revaId).execute(serviceApi, credentials!);
        dispatch(setDocuments(documents));
    } catch (err) {
        dispatch(setQueryError(generateDefaultQueryError(err as Error)));
    }
};

/**
 * Reload the document list, but only if it does not already contain a document with this filename.
 *
 * For the endpoints that create a valuation PDF on demand and archive it on the way
 * (`fetchOrCreateSnapshotPdf`): an already archived PDF is re-delivered under its existing name, a freshly
 * created one arrives under a name the list cannot know yet. Comparing the name therefore tells the two
 * apart without asking the API again. A name that differs for other reasons (missing Content-Disposition
 * header, different unicode normalization) costs one superfluous reload — it can never miss one, since the
 * server only re-delivers a document whose name is already in the list.
 *
 * Does nothing while the list has never been loaded: the mount effects of the pages that show documents
 * fetch it anyway, and {@link loadDocumentsAsync} would report an error for a valuation without value
 * orders — which must not happen as a side effect of downloading a PDF.
 */
export const reloadDocumentsIfMissing =
    (filename: string | null | undefined): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const state = getState();
        if (!selectInitialized(state)) return;
        const documents = selectDocuments(state) ?? [];
        if (filename && documents.some((document) => document.filename === filename)) return;
        await dispatch(loadDocumentsAsync());
    };

/** What {@link downloadDocumentAsync} needs to address a document — callers may hand it a full document. */
export type DownloadableDocument = Pick<ValueOrderDocument, "valueOrderGuid" | "documentId" | "extension">;

/**
 * Download document from list via API.
 * @param document the document to download
 */
export const downloadDocumentAsync =
    (document: DownloadableDocument): RevaThunkAction<Promise<Blob>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;

        const state = getState();

        const revaId = selectRevaId(state);
        const credentials = selectCredentials(state);

        if (!revaId || !document) {
            throw new DownloadToastMessage("No document selected");
        }

        try {
            return await new DocumentDownloadApiInterface(
                revaId,
                document.valueOrderGuid,
                document.documentId,
                document.extension,
            ).execute(serviceApi, credentials!);
        } catch (err) {
            console.error("Error downloading document", document, err);
            throw new DownloadToastMessage("Error downloading document");
        }
    };

export class DownloadToastMessage {
    errorMessage: string;

    constructor(errorMessage: string) {
        this.errorMessage = errorMessage;
    }
}

/**
 * Upload document from list via API.
 * @param document the uploaded document
 * @param type its document type
 * @return document id
 */
export const uploadDocumentAsync =
    (document: File, type: string): RevaThunkAction<Promise<string | undefined>> =>
    async (dispatch, getState, extraArgument) => {
        const {serviceApi} = extraArgument;
        const state = getState();
        const revaId = selectRevaId(state);
        const credentials = selectCredentials(state);

        try {
            const {documentId} = await new DocumentUploadApiInterface(revaId!, type, document).execute(
                serviceApi,
                credentials!,
            );
            return documentId;
        } catch (err) {
            dispatch(
                setQueryError({
                    error: err instanceof ErrorI18n ? err.message : "general.error.custom",
                    errorArgs: err instanceof ErrorI18n ? {...err.messageArgs} : {errorMessage: (err as Error).message},
                    errorHasContactLink: true,
                    keepDocuments: true,
                }),
            );
        }
    };
